import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { scrollToSection } from "@/scroll";

const links = [
  { id: "manifesto", sel: "#manifesto", testid: "nav-link-manifesto" },
  { id: "pillars", sel: "#pilastri", testid: "nav-link-pillars" },
  { id: "events", sel: "#eventi", testid: "nav-link-events" },
  { id: "media", sel: "#media", testid: "nav-link-media" },
  { id: "community", sel: "#community", testid: "nav-link-community" },
];

export default function Nav() {
  const { lang, setLang, t } = useLanguage();
  const { user, logout, setAuthOpen } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (sel) => {
    setMenuOpen(false);
    scrollToSection(sel);
  };

  const handleLogout = async () => {
    await logout();
    toast(t.auth.loggedOut);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[120] transition-[background-color,border-color] duration-300 border-b ${
        scrolled
          ? "bg-[#120F0D]/85 backdrop-blur-md border-sand/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <button
          data-testid="brand-logo-dust-republic"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <img src="/logo.png" alt="Dust Republic" className="h-11 w-auto group-hover:scale-[1.03] transition-transform duration-500" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.sel)}
              className="font-telemetry text-[11px] uppercase tracking-[0.22em] text-ash hover:text-blaze transition-colors duration-200"
            >
              {t.nav[l.id]}
            </button>
          ))}
          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="font-telemetry text-[11px] uppercase tracking-[0.22em] text-blaze border border-blaze/40 px-2.5 py-1 hover:bg-blaze hover:text-ink transition-colors duration-200"
            >
              HQ
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="language-toggle-button"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            className="font-telemetry text-[11px] tracking-[0.2em] uppercase border border-line px-3 py-1.5 text-sand hover:border-blaze hover:text-blaze transition-colors duration-200"
          >
            {lang === "it" ? "IT / EN" : "EN / IT"}
          </button>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/profilo"
                data-testid="member-profile-badge"
                className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.15em] text-sand border border-blaze/50 bg-blaze/10 px-3 py-1.5 hover:bg-blaze hover:text-ink transition-colors duration-200"
              >
                <UserRound size={13} />
                {user.name?.split(" ")[0]}
              </Link>
              <button
                data-testid="member-logout-btn"
                onClick={handleLogout}
                title={t.nav.logout}
                className="border border-line p-1.5 text-ash hover:text-blaze hover:border-blaze transition-colors duration-200"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button
              data-testid="member-login-modal-open-btn"
              onClick={() => setAuthOpen(true)}
              className="hidden sm:block font-telemetry text-[11px] uppercase tracking-[0.2em] bg-blaze text-ink px-4 py-1.5 hover:bg-sand transition-colors duration-200"
            >
              {t.nav.login}
            </button>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden text-sand p-1.5 border border-line"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#120F0D]/95 backdrop-blur-md border-t border-line"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.sel)}
                  className="text-left font-telemetry text-xs uppercase tracking-[0.22em] text-ash hover:text-blaze transition-colors"
                >
                  {t.nav[l.id]}
                </button>
              ))}
              {!user && (
                <button
                  data-testid="mobile-member-login-btn"
                  onClick={() => {
                    setMenuOpen(false);
                    setAuthOpen(true);
                  }}
                  className="mt-2 font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-4 py-2.5 w-max"
                >
                  {t.nav.login}
                </button>
              )}
              {user && (
                <button
                  data-testid="mobile-member-logout-btn"
                  onClick={handleLogout}
                  className="mt-2 font-telemetry text-xs uppercase tracking-[0.2em] border border-line text-ash px-4 py-2.5 w-max"
                >
                  {t.nav.logout}
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
